/**
 * Types for Backtest Configuration (Frontend)
 * 
 * These types mirror the backend types but are adapted for frontend form handling
 */

/**
 * Commission configuration
 */
export interface CommissionConfig {
  type: 'fixed' | 'percentage' | 'per_contract';
  value: number;                    // Fixed amount, percentage (0.01 = 1%), or per contract
  applyOnEntry?: boolean;           // Apply commission on entry (default: true)
  applyOnExit?: boolean;            // Apply commission on exit (default: true)
}

/**
 * Slippage configuration
 */
export interface SlippageConfig {
  type: 'fixed' | 'percentage';
  value: number;                    // Fixed points or percentage (0.001 = 0.1%)
  applyOnEntry?: boolean;           // Apply slippage on entry (default: true)
  applyOnExit?: boolean;            // Apply slippage on exit (default: true)
}

/**
 * Trailing stop loss configuration
 */
export interface TrailingStopConfig {
  enabled: boolean;
  activationPrice?: number;         // Price movement to activate trailing (percentage or points)
  trailAmount?: number;             // Amount to trail (percentage or points)
  trailType?: 'percentage' | 'points';
}

/**
 * Break-even stop loss configuration
 */
export interface BreakEvenConfig {
  enabled: boolean;
  activationProfit?: number;        // Profit percentage to activate break-even (e.g., 0.01 = 1%)
  offset?: number;                  // Offset from entry price (percentage or points)
}

/**
 * Trading filters configuration
 */
export interface TradingFilters {
  allowedHours?: Array<{ start: number; end: number }>; // Hours in 24h format (e.g., { start: 9, end: 16 })
  allowedDays?: number[];           // Days of week (0 = Sunday, 6 = Saturday)
  allowedSessions?: string[];       // Session identifiers (e.g., ['regular', 'premarket'])
  timezone?: string;                // Timezone for hour/day filtering (e.g., 'America/New_York')
}

/**
 * Capital management configuration
 */
export interface CapitalManagement {
  type: 'fixed' | 'percentage' | 'risk_per_trade';
  value: number;                    // Fixed amount, percentage of capital, or risk percentage
  maxRiskPerTrade?: number;         // Maximum risk per trade as percentage (for risk_per_trade type)
}

/**
 * Strategy metadata configuration (for backtesting)
 */
export interface StrategyMetadata {
  signals: {
    entry: {
      long: string[];         // Signal names for long entries
      short: string[];        // Signal names for short entries
    };
    exit: {
      long: string[];        // Signal names for long exits
      short: string[];        // Signal names for short exits
    };
  };
  stop_loss: {
    long?: {
      use_signal_level?: boolean;
      signal_name?: string;
      default_multiplier?: number;
      default_based_on?: 'atr' | 'price' | 'percent';
    };
    short?: {
      use_signal_level?: boolean;
      signal_name?: string;
      default_multiplier?: number;
      default_based_on?: 'atr' | 'price' | 'percent';
    };
    // Legacy support: if long/short not specified, use these as defaults
    use_signal_level?: boolean;
    signal_name?: string;
    default_multiplier?: number;
    default_based_on?: 'atr' | 'price' | 'percent';
  };
  take_profit: {
    long?: {
      use_signal_level?: boolean;
      signal_name?: string;
      default_multiplier?: number;
      default_based_on?: 'atr' | 'price' | 'percent';
    };
    short?: {
      use_signal_level?: boolean;
      signal_name?: string;
      default_multiplier?: number;
      default_based_on?: 'atr' | 'price' | 'percent';
    };
    // Legacy support: if long/short not specified, use these as defaults
    use_signal_level?: boolean;
    signal_name?: string;
    default_multiplier?: number;
    default_based_on?: 'atr' | 'price' | 'percent';
  };
  position_management?: {
    max_positions?: number;
    allow_multiple_entries?: boolean;
    close_on_opposite_signal?: boolean;
  };
}

/**
 * Complete backtest configuration form
 */
export interface BacktestConfigForm {
  // Basic configuration
  initialCapital: number;
  signalDatasetId?: string;           // Dataset ID for signal generation
  executionDatasetId?: string;        // Dataset ID for trade execution
  signalTimeframe?: string;           // "5min", "15min", etc. (legacy support, derived from dataset)
  executionTimeframe?: string;        // "1min", "5min", etc. (legacy support, derived from dataset)
  positionSize?: number;              // Position size (deprecated, use capitalManagement)

  // Strategy Metadata
  strategyMetadata: StrategyMetadata;

  // Performance optimizations
  enableCandleSkipping?: boolean;     // Enable candle skipping when no positions/signals

  // Position management
  closeEndOfDay?: boolean;            // Close all positions at end of trading day
  leverage?: number;                  // Leverage multiplier
  marginRequirement?: number;         // Margin requirement as percentage

  // Commissions and Slippage
  commission?: CommissionConfig;
  slippage?: SlippageConfig;

  // Advanced configuration
  trailingStop?: TrailingStopConfig;
  breakEven?: BreakEvenConfig;
  tradingFilters?: TradingFilters;
  capitalManagement?: CapitalManagement;
}

/**
 * Backtest preset configuration
 */
export interface BacktestPreset {
  name: string;
  description: string;
  config: Partial<BacktestConfigForm>;
}

/**
 * Timeframe options for UI
 */
export interface TimeframeOption {
  label: string;
  value: string;
  seconds: number;
}

/**
 * Available timeframes
 */
export const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { label: '1 minute', value: '1min', seconds: 60 },
  { label: '5 minutes', value: '5min', seconds: 300 },
  { label: '15 minutes', value: '15min', seconds: 900 },
  { label: '30 minutes', value: '30min', seconds: 1800 },
  { label: '1 hour', value: '1h', seconds: 3600 },
  { label: '4 hours', value: '4h', seconds: 14400 },
  { label: '1 day', value: '1d', seconds: 86400 },
];

/**
 * Convert timeframe string to seconds
 */
export function timeframeToSeconds(timeframe: string): number {
  const option = TIMEFRAME_OPTIONS.find(opt => opt.value === timeframe);
  return option?.seconds || 300; // Default to 5min
}

/**
 * Convert seconds to timeframe string
 */
export function secondsToTimeframe(seconds: number): string {
  const option = TIMEFRAME_OPTIONS.find(opt => opt.seconds === seconds);
  return option?.value || '5min';
}
